import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isOne } from '@utils/validations'

import { LeverageTypeDto, isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'

export const useLeverageSelect = (selectedPlatformType?: NameDto<PlatformTypeEnum>) => {
  const apiClient = useApiClient(ClientApiClient)

  const { data: leverageTypes, isLoading } = useQuery({
    queryKey: queryKeys.tradingAccount.leverageTypes(selectedPlatformType?.id),
    queryFn: () => apiClient.getLeverageTypes(selectedPlatformType?.id),
  })

  const selectDynamicLeverageType = (leverageTypes: LeverageTypeDto[]) => {
    return leverageTypes.find((leverage) => isLeverageTypeDynamic(leverage.id))
  }

  const isDynamicLeverageType = (platformType?: NameDto<PlatformTypeEnum>) => {
    const platformTypeId = platformType?.id
    return isTradingViewPlatformType(platformTypeId) || isTMTPlatformType(platformTypeId)
  }

  const selectFirstLeverageType = (leverageTypes: LeverageTypeDto[]) => {
    if (isOne(leverageTypes.length)) {
      const [leverageType] = leverageTypes
      return leverageType
    }
  }

  const leverageType = useMemo(() => {
    if (leverageTypes) {
      if (isDynamicLeverageType(selectedPlatformType)) {
        const dynamicLeverageType = selectDynamicLeverageType(leverageTypes)
        if (dynamicLeverageType) {
          return dynamicLeverageType
        }
        return selectFirstLeverageType(leverageTypes)
      }
      return selectFirstLeverageType(leverageTypes)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatformType?.id, leverageTypes, isLoading])

  return { leverageTypes, leverageType }
}
